<template>
  <div
    ref="root"
    class="ak-color-picker__color-alpha"
    @mousedown.prevent.stop="selectAlpha"
  >
    <canvas ref="canvasAlpha" />
    <div :style="slideAlphaStyle" class="ak-color-picker__slide" />
  </div>
</template>

<script>
import { defineComponent, onMounted, ref, watch } from 'vue'
import colorPickerMixin from '../../mixins/colorPickerMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-color-picker-alpha />
   */
  name: 'AkColorPickerAlpha',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    color: {
      type: String,
      default: '#000000',
    },

    rgba: {
      type: Object,
      default: null,
    },

    width: {
      type: Number,
      default: 15,
    },

    height: {
      type: Number,
      default: 152,
    },
  },

  emits: ['selectAlpha'],

  setup(props, { emit }) {
    const slideAlphaStyle = ref({})
    const alphaSize = ref(5)
    const canvasAlpha = ref(null)
    const root = ref(null)

    const renderColor = () => {
      const mixin = colorPickerMixin()
      const canvas = canvasAlpha.value
      const width = props.width
      const height = props.height
      const size = alphaSize.value
      const canvasSquare = mixin.createAlphaSquare(size)
      const ctx = canvas.getContext('2d')

      canvas.width = width
      canvas.height = height
      ctx.fillStyle = ctx.createPattern(canvasSquare, 'repeat')
      ctx.fillRect(0, 0, width, height)

      mixin.createLinearGradient(
        'p',
        ctx,
        width,
        height,
        'rgba(255,255,255,0)',
        props.color
      )
    }

    const renderSlide = () => {
      slideAlphaStyle.value = {
        top: props.rgba.a * props.height - 2 + 'px',
      }
    }

    const selectAlpha = (e) => {
      const { top: hueTop } = root.value.getBoundingClientRect()

      const mousemove = (e) => {
        let y = e.clientY - hueTop
        if (y < 0) {
          y = 0
        }
        if (y > props.height) {
          y = props.height
        }
        let a = parseFloat((y / props.height).toFixed(2))
        emit('selectAlpha', a)
      }

      mousemove(e)

      const mouseup = () => {
        document.removeEventListener('mousemove', mousemove)
        document.removeEventListener('mouseup', mouseup)
      }

      document.addEventListener('mousemove', mousemove)
      document.addEventListener('mouseup', mouseup)
    }

    watch(
      () => props.rgba,
      () => {
        renderSlide()
      },
      { deep: true }
    )

    watch(
      () => props.color,
      () => {
        renderColor()
      }
    )

    onMounted(() => {
      renderColor()
      renderSlide()
    })

    return {
      slideAlphaStyle,
      alphaSize,
      renderColor,
      renderSlide,
      selectAlpha,
      canvasAlpha,
      root,
    }
  },
})
</script>

<style lang="scss" rel="stylesheet/scss">
.ak-color-picker {
  &__color-alpha {
    position: relative;
    margin-left: 8px;
    cursor: pointer;
  }

  &__slide {
    position: absolute;
    left: 0;
    top: 100px;
    width: 100%;
    height: 4px;
    background: #fff;
    box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.3);
    pointer-events: none;
  }
}
</style>
